import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, Subject } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import { OrderService } from '../../../services/order.service';
import { ProductService } from '../../../services/product.service';
import { AccountService } from '../../../services/account.service';
import { AuthService } from '../../../services/auth.service';
import { NewOrder, OrderLocation, OrderType, PLATFORM_OPTIONS, StockViolation } from '../../../domain/order.model';
import { Product, ProductSearchParams } from '../../../domain/product.model';
import { Account, PageableAccount } from '../../../domain/account.model';
import { AccountDropdown } from '../../../domain/accountDropdown.model';

interface CartItem {
  productId: number;
  sku?: string;
  description?: string;
  variant?: string;
  uom?: string;
  availableQuantity: number;
  quantity: number;
  comments: string;
  insufficientStock?: boolean;
}

@Component({
  selector: 'app-create-order',
  standalone: false,
  templateUrl: './create-order.component.html',
  styleUrl: './create-order.component.scss',
  providers: [MessageService, ConfirmationService]
})
export class CreateOrderComponent implements OnInit {
  activeStep = 1;
  submitting = false;
  createdOrderId: number | null = null;

  accounts: any[] = [];
  userAccount: Account = {};
  accountLoading = false;
  searchAccountSubject = new Subject<string>();
  searchAccountQuery = '';

  platformOptions = PLATFORM_OPTIONS;
  orderTypeOptions = [
    { label: 'Entrega', value: OrderType.ENTREGA },
    { label: 'Recepción', value: OrderType.RECEPCION },
  ];
  locationOptions = [
    { label: 'Domicilio', value: OrderLocation.DOMICILIO },
    { label: 'Bodega', value: OrderLocation.BODEGA },
  ];

  infoForm: FormGroup = this.fb.group({
    accountId: [null, Validators.required],
    orderType: [null, Validators.required],
    scheduledDate: [null, Validators.required],
    titular: [''],
    description: [''],
    location: [null, Validators.required],
    platform: [null, Validators.required],
    address: [''],
  });

  // Paso 2 — catalog browser (left) + cart (right)
  catalogProducts: Product[] = [];
  catalogTotalRecords = 0;
  catalogLoading = false;
  catalogRows = 10;
  catalogSearchTerm = '';
  catalogSearchSubject = new Subject<string>();
  lastCatalogEvent: any = null;

  selectedItems: CartItem[] = [];
  activeCommentItem: CartItem | null = null;

  stockWarningMessage: string | null = null;
  minDate: Date = new Date();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private orderService: OrderService,
    private productService: ProductService,
    private accountService: AccountService,
    private authService: AuthService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) { }

  ngOnInit(): void {
    this.fetchInitialData();

    this.searchAccountSubject.pipe(debounceTime(800)).subscribe(searchText => {
      this.searchAccountQuery = searchText;
      this.getAccounts();
    });

    this.catalogSearchSubject.pipe(debounceTime(500)).subscribe(term => {
      this.catalogSearchTerm = term;
      this.reloadCatalog();
    });

    // Dirección only applies to Domicilio — clear it (and its validator) the moment the
    // user switches away, so a hidden field never leaves stale required state behind.
    this.infoForm.get('location')?.valueChanges.subscribe(location => {
      const addressControl = this.infoForm.get('address');
      if (location === OrderLocation.DOMICILIO) {
        addressControl?.setValidators([Validators.required]);
      } else {
        addressControl?.reset('');
        addressControl?.clearValidators();
      }
      addressControl?.updateValueAndValidity();
    });

    // Products are account-scoped — a product picked under account A means nothing once
    // the account changes, so the cart and catalog browser both reset.
    this.infoForm.get('accountId')?.valueChanges.subscribe(() => {
      this.selectedItems = [];
      this.catalogProducts = [];
      this.catalogTotalRecords = 0;
      this.reloadCatalog();
    });
  }

  get hasAccountAccess(): boolean {
    return this.authService.canAccessAction('orders', 'accountSelection');
  }

  get isEntrega(): boolean {
    return this.infoForm.get('orderType')?.value === OrderType.ENTREGA;
  }

  // RECEPCION-only: lets a BILD role (createEmpty) create the order without knowing its
  // products yet — e.g. a client renting warehouse space whose items aren't registered
  // until they physically arrive. Added later via editing the order (orders.component.ts).
  get canCreateEmptyOrder(): boolean {
    return !this.isEntrega && this.authService.canAccessAction('orders', 'createEmpty');
  }

  get selectedAccountName(): string {
    const accountId = this.infoForm.get('accountId')?.value;
    return this.accounts.find((a: any) => a.accountId === accountId)?.name ?? '';
  }

  get totalSelectedQuantity(): number {
    return this.selectedItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
  }

  //--- Paso 1: account dropdown ---

  fetchInitialData() {
    if (this.hasAccountAccess) {
      this.getAccounts();
    } else {
      this.getAccount();
    }
  }

  loadAccounts(event: any) {
    this.searchAccountSubject.next(event.filter || '');
  }

  getAccounts() {
    this.accountLoading = true;
    this.accountService.searchAllAccounts(this.searchAccountQuery).subscribe((response: PageableAccount) => {
      this.accounts = response.content;
      this.accountLoading = false;
    });
  }

  getAccount() {
    this.infoForm.get('accountId')?.disable();
    this.accountLoading = true;
    this.userAccount = this.authService.getUserAccount();
    this.infoForm.patchValue({ accountId: this.userAccount.accountId });
    this.accountService.getDropdownAccount(this.userAccount.accountId).subscribe((response: AccountDropdown[]) => {
      this.accounts = response;
      this.accountLoading = false;
    });
  }

  //--- Paso 2: catalog browser ---

  onCatalogSearch(term: string) {
    this.catalogSearchSubject.next(term);
  }

  reloadCatalog() {
    this.loadCatalogProducts({ ...(this.lastCatalogEvent ?? {}), first: 0, rows: this.catalogRows });
  }

  loadCatalogProducts(event: any) {
    const accountId = this.infoForm.get('accountId')?.value;
    this.lastCatalogEvent = event;
    if (!accountId) {
      this.catalogProducts = [];
      this.catalogTotalRecords = 0;
      return;
    }

    this.catalogLoading = true;
    const params: ProductSearchParams = {
      accountId,
      generalSearch: this.catalogSearchTerm || undefined,
      page: event?.first != null && event?.rows ? event.first / event.rows : 0,
      size: event?.rows ?? this.catalogRows,
      sortBy: 'productId',
      sortDirection: 'ASC',
    };

    this.productService.searchProducts(params).subscribe({
      next: (data) => {
        this.catalogProducts = data.content;
        this.catalogTotalRecords = data.totalElements;
        this.catalogLoading = false;
      },
      error: () => {
        this.catalogLoading = false;
      }
    });
  }

  isInCart(productId: number): boolean {
    return this.selectedItems.some(i => i.productId === productId);
  }

  canAddToCart(product: Product): boolean {
    if (this.isInCart(product.productId)) return false;
    if (this.isEntrega && (product.availableQuantity ?? 0) <= 0) return false;
    return true;
  }

  addToCart(product: Product) {
    if (!this.canAddToCart(product)) return;
    this.selectedItems = [...this.selectedItems, {
      productId: product.productId,
      sku: product.sku,
      description: product.description,
      variant: product.variant,
      uom: product.uom,
      availableQuantity: product.availableQuantity ?? 0,
      quantity: 1,
      comments: '',
    }];
  }

  removeFromCart(item: CartItem) {
    this.selectedItems = this.selectedItems.filter(i => i !== item);
  }

  openCommentPopover(event: Event, item: CartItem, popover: any) {
    this.activeCommentItem = item;
    popover.toggle(event);
  }

  //--- Step navigation ---

  goToStep(step: number) {
    if (step >= 2 && this.infoForm.invalid) {
      this.infoForm.markAllAsTouched();
      this.messageService.add({ severity: 'warn', summary: 'Datos incompletos', detail: 'Completa la información de la orden antes de continuar.' });
      return;
    }
    if (step >= 3 && this.selectedItems.length === 0 && !this.canCreateEmptyOrder) {
      this.messageService.add({ severity: 'warn', summary: 'Sin productos', detail: 'Selecciona al menos un producto.' });
      return;
    }
    this.stockWarningMessage = null;
    this.activeStep = step;
  }

  //--- Paso 3: confirm (validate stock, then create) ---

  confirmOrder() {
    this.submitting = true;
    const accountId = this.infoForm.value.accountId;
    const orderType = this.infoForm.value.orderType;

    this.orderService.validateStock({
      accountId,
      orderType,
      items: this.selectedItems.map(i => ({ productId: i.productId, quantity: i.quantity })),
    }).subscribe({
      next: (violations) => {
        if (violations.length) {
          this.applyViolations(violations, 'Algunos productos ya no tienen suficiente stock disponible. Ajusta las cantidades marcadas en rojo.');
          this.submitting = false;
          return;
        }
        this.createOrder();
      },
      error: () => {
        this.submitting = false;
      }
    });
  }

  private applyViolations(violations: StockViolation[], message: string) {
    const byProduct = new Map(violations.map(v => [v.productId, v]));
    this.selectedItems.forEach(item => {
      const violation = byProduct.get(item.productId);
      item.insufficientStock = !!violation;
      if (violation) {
        item.availableQuantity = violation.availableQuantity;
      }
    });
    this.stockWarningMessage = message;
    this.activeStep = 2;
  }

  private createOrder() {
    const value = this.infoForm.value;
    const payload: NewOrder = {
      accountId: value.accountId,
      orderType: value.orderType,
      description: value.description || undefined,
      titular: value.titular || undefined,
      scheduledDate: value.scheduledDate,
      location: value.location,
      platform: value.platform,
      address: value.location === OrderLocation.DOMICILIO ? value.address : undefined,
      items: this.selectedItems.map(i => ({
        productId: i.productId,
        quantity: i.quantity,
        comments: i.comments || undefined,
      })),
    };

    this.orderService.createOrder(payload).subscribe({
      next: (order) => {
        this.createdOrderId = order.orderId ?? null;
        this.submitting = false;
        this.activeStep = 4;
      },
      error: (error) => {
        this.submitting = false;
        const violations: StockViolation[] | undefined = error?.error?.violations;
        if (violations?.length) {
          this.applyViolations(violations, error?.error?.message ?? 'Algunos productos ya no tienen suficiente stock disponible.');
          return;
        }
        this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.message ?? 'No se pudo crear la orden' });
      }
    });
  }

  //--- Paso 4 ---

  createAnotherOrder() {
    const accountId = this.infoForm.value.accountId;
    this.activeStep = 1;
    this.createdOrderId = null;
    this.selectedItems = [];
    this.stockWarningMessage = null;
    this.infoForm.reset();
    if (!this.hasAccountAccess) {
      this.infoForm.patchValue({ accountId: this.userAccount.accountId });
    } else {
      this.infoForm.patchValue({ accountId });
    }
  }

  goToOrders() {
    this.router.navigateByUrl('/home/orders');
  }

  cancelOrderCreation() {
    this.confirmationService.confirm({
      header: 'Cancelar orden',
      message: '¿Seguro que quieres cancelar? Se perderá la información capturada.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí',
      rejectLabel: 'No',
      accept: () => this.goToOrders(),
    });
  }
}
